import type { LangCode } from '@/types/config'
import type { MemberNoticeDTO } from '@/types/notice'

type NoticeLangKey = 'zh' | 'en' | 'vi' | 'zh_hant' | 'km' | 'hi' | 'ta' | 'ms' | 'pt'

type NoticeLangMap = Partial<Record<NoticeLangKey, string>>

const noticeLocaleKeyMap: Record<LangCode, NoticeLangKey> = {
  'zh-CN': 'zh',
  'en-US': 'en',
  'vi-VN': 'vi',
  'hi-IN': 'hi',
  'ta-IN': 'ta',
  'ms-MY': 'ms',
  'pt-BR': 'pt'
}

const fallbackLangKeys: NoticeLangKey[] = ['zh', 'en']

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== ''

const pickFirstNonEmptyValue = (langMap: NoticeLangMap): string | undefined =>
  Object.values(langMap).find(isNonEmptyString)

const parseMultiLangObject = (value: string): NoticeLangMap | null => {
  try {
    const parsed: unknown = JSON.parse(value)

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }

    return parsed as NoticeLangMap
  } catch {
    return null
  }
}

const resolveMultiLangText = (value: string, locale: LangCode): string => {
  const langMap = parseMultiLangObject(value)

  if (!langMap) {
    return value
  }

  const preferredKeys = Array.from(new Set([noticeLocaleKeyMap[locale], ...fallbackLangKeys]))

  for (const key of preferredKeys) {
    const langValue = langMap[key]
    if (isNonEmptyString(langValue)) {
      return langValue
    }
  }

  return pickFirstNonEmptyValue(langMap) || value
}

export const formatNoticeMultiLang = (
  list: MemberNoticeDTO[] = [],
  locale: LangCode
): MemberNoticeDTO[] =>
  list.map((item) => ({
    ...item,
    title: resolveMultiLangText(item.title, locale),
    content: resolveMultiLangText(item.content, locale)
  }))
